import React, { useRef, useState, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { useNavigate } from 'react-router-dom';
import { X, Search, ArrowDownToLine, ArrowUpFromLine, CheckCircle } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import io from 'socket.io-client';

import { state } from '../store/global';
import API from '../lib/api';
import findMessage from '../utils/messages';

import Notification from '../components/common/Notification';
import IconButton from '../components/common/IconButton';
import AddAssetModal from '../components/modal/AddAssetModal';
import ComeBack from '../components/common/ComeBack';
import NormalButton from '../components/common/NormalButton';

import PlusIcon from '../assets/images/plus-white.svg';
import LogoIcon from '../assets/images/logo_no_letter.svg';
import PseIcon from '../assets/images/pse.png';
import Pse2xIcon from '../assets/images/pse_2x.png';
import colombiaFlag from '../assets/images/country/colombia.png';
import BlockchainIcon from '../components/svg/BlockchainIcon';
import ArrowCircleUpRightIcon from '../components/svg/ArrowCircleUpRightIcon';
import ExchangeRate from '../components/common/ExchangeRate';
import { GettingNativeAsset } from '../utils/constants';
import { getAssetImage } from '../utils/cropUtils';

export default function Account() {
  const snap = useSnapshot(state);
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);
  const [vault, setVault] = useState({});
  const [assets, setAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [isAddModal, setIsAddModal] = useState(false);
  const [isWithdrawModal, setIsWithdrawModal] = useState(false);
  const [withdrawAsset, setWithdrawAsset] = useState(null);
  const [withdrawTab, setWithdrawTab] = useState('internal');
  const [isDepositModal, setIsDepositModal] = useState(false);
  const [depositAsset, setDepositAsset] = useState(null);
  const [depositProgress, setDepositProgress] = useState(0);
  const [depositType, setDepositType] = useState(''); // 'internal' or 'external'
  const [userVaults, setUserVaults] = useState([]);

  // Withdrawal form state
  const [internalVaultAddress, setInternalVaultAddress] = useState('');
  const [externalAddress, setExternalAddress] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [searchVault, setSearchVault] = useState('');
  const [searchAsset, setSearchAsset] = useState('');

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Phone verification state
  const [showPhoneVerifyModal, setShowPhoneVerifyModal] = useState(false);
  const [verifyCode, setVerifyCode] = useState(['', '', '', '', '', '']);
  const [verifyError, setVerifyError] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const verifyInputRefs = useRef([]);

  // Deposit form state
  const [depositSelectedVault, setDepositSelectedVault] = useState('');
  const [depositExternalAddress, setDepositExternalAddress] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [depositSearchVault, setDepositSearchVault] = useState('');
  const [depositWalletAddress, setDepositWalletAddress] = useState('');
  const [copied, setCopied] = useState(false);

  // PSE Deposit state
  const [depositCopValue, setDepositCopValue] = useState('');
  const [depositAssetValue, setDepositAssetValue] = useState('');
  const [depositCheckoutLink, setDepositCheckoutLink] = useState('');
  const [depositExchangeRate, setDepositExchangeRate] = useState(0);
  const [depositSubmitting, setDepositSubmitting] = useState(false);
  const [assetUsdPrice, setAssetUsdPrice] = useState(1); // Price of 1 asset in USD
  const [totalBalance, setTotalBalance] = useState(0.0);

  // Socket.IO reference and token
  const socketRef = useRef(null);
  const token = localStorage.getItem('token');

  const Header = ['Nombre del activo', 'Bloque donde tienes el activo', 'Balance', 'Action'];

  const getAllAssets = async () => {
    try {
      const res = await API.get('/assets/getAssetApproved');

      if (res.data.success == 'success') {
        setAssets(res.data.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getVautByUserId = async () => {
    try {
      const res = await API.get('/user/getVaultByUserId');

      if (res.data.success == 'success') {
        console.log(res.data);
        setVault(res.data.data);
        setTotalBalance(res.data.totalBalance);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getUserVaults = async () => {
    try {
      const res = await API.get('/user/getAllVaults');
      if (res.data.success == 'success') {
        setUserVaults(res.data.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleConfirmSelection = async () => {
    try {
      setIsSubmitting(true);
      const data = {
        vaultAccountId: vault.vault_id,
        assetId: selectedAsset.legacyId,
        asset_id: selectedAsset._id,
      };
      console.log(data);
      const res = await API.post('/assets/addVaultAccountAssets', data);

      if (res.data.success == 'success') {
        setNotification({
          message: 'El activo se ha agregado correctamente.',
          type: 'success',
        });

        getVautByUserId();
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      console.log(msg);
      setNotification({
        message: findMessage(msg),
        type: 'error',
      });
    }
    setIsAddModal(false);
    setIsSubmitting(false);
  };

  const handleWithdraw = (asset) => {
    setWithdrawAsset(asset);
    setIsWithdrawModal(true);
    setWithdrawTab('internal');
    setExternalAddress('');
    setWithdrawAmount('');
    setSearchVault('');
    setSearchAsset('');
    setShowConfirmModal(false);
    setShowPhoneVerifyModal(false);
  };

  const handleDeposit = (asset) => {
    setDepositAsset(asset);
    setIsDepositModal(true);
    setDepositProgress(0);
    setDepositType('');
    setDepositSelectedVault('');
    setDepositExternalAddress('');
    setDepositAmount('');
    setDepositSearchVault('');
    setDepositWalletAddress('');
    setDepositCopValue('');
    setDepositAssetValue('');
    setCopied(false);

    // Get asset details and set price (default to 1 for stablecoins)
    const assetDetails = getAssetDetails(asset.assetId);
    const symbol = assetDetails.displaySymbol?.toLowerCase() || '';
    // For USDC/USDT assume 1:1 with USD, could fetch real price from API
    setAssetUsdPrice(1);
  };

  const handleWithdrawValidation = () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      setNotification({
        message: 'Por favor ingrese un monto válido',
        type: 'error',
      });
      return false;
    }

    if (parseFloat(withdrawAmount) > withdrawAsset.balance) {
      setNotification({
        message: 'Monto insuficiente en el balance',
        type: 'error',
      });
      return false;
    }

    if (withdrawTab === 'internal' && !internalVaultAddress) {
      setNotification({
        message: 'Por favor ingrese un ID de bóveda de destino',
        type: 'error',
      });
      return false;
    }

    if (withdrawTab === 'external' && !externalAddress) {
      setNotification({
        message: 'Por favor ingrese una dirección externa',
        type: 'error',
      });
      return false;
    }

    return true;
  };

  const handleShowConfirmation = () => {
    if (handleWithdrawValidation()) {
      setShowConfirmModal(true);
    }
  };

  // Phone verification functions
  const onPhoneVerify = async () => {
    if (sendingCode) return;

    setSendingCode(true);
    try {
      const phoneNumber =
        snap.user.phonenumber.nationality.area_code + snap.user.phonenumber.number;

      console.log(phoneNumber);

      const result = await API.post('auth/sendCodeToPhone', {
        phonenumber: phoneNumber,
      });
      console.log(result);
      if (result.data.message === 'success') {
        setShowConfirmModal(false);
        setShowPhoneVerifyModal(true);
        setVerifyCode(['', '', '', '', '', '']);
        setVerifyError(false);
      } else {
        setNotification({
          message: 'Código de verificación enviado a tu teléfono',
          type: 'success',
        });
      }
    } catch (err) {
      console.log('err: ', err);
      setNotification({
        message: findMessage('Error al enviar código de verificación'),
        type: 'error',
      });
    } finally {
      setSendingCode(false);
    }
  };

  const onConfirmPhoneVerify = async () => {
    if (verifyingCode) return;

    const code = verifyCode.join('');
    if (code.length !== 6) {
      setVerifyError(true);
      return;
    }

    setVerifyingCode(true);
    try {
      const phoneNumber =
        snap.user.phonenumber.nationality.area_code + snap.user.phonenumber.number;

      const result = await API.post('auth/verifyCode', {
        phonenumber: phoneNumber,
        code: code,
      });

      if (result.data.phoneVerified == true) {
        setShowPhoneVerifyModal(false);
        setNotification({
          message: 'Verificación exitosa',
          type: 'success',
        });

        // Now proceed with withdrawal
        await handleWithdrawSubmit();
      }
    } catch (err) {
      console.log('err: ', err);
      setVerifyError(true);
      setNotification({
        message: findMessage('Código de verificación incorrecto'),
        type: 'error',
      });
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleVerifyCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...verifyCode];
    newCode[index] = value.slice(-1);
    setVerifyCode(newCode);
    setVerifyError(false);

    // Auto-focus next input
    if (value && index < 5) {
      verifyInputRefs.current[index + 1]?.focus();
    }
  };

  const handleVerifyCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verifyCode[index] && index > 0) {
      verifyInputRefs.current[index - 1]?.focus();
    }
  };

  const handleWithdrawSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const data = {
        assetId: withdrawAsset.assetId,
        amount: withdrawAmount,
        type: withdrawTab,
      };

      if (withdrawTab === 'internal') {
        data.toAddress = internalVaultAddress;
      } else {
        data.toAddress = externalAddress;
      }

      const res = await API.post('/transaction/transferAssetToInternalUser', data);

      if (res.data.message == 'Transaction created successfully') {
        setNotification({
          message: 'Retiro procesado exitosamente',
          type: 'success',
        });
        setShowConfirmModal(false);
        setIsWithdrawModal(false);
        getVautByUserId();
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      setNotification({
        message: findMessage(msg),
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDepositTypeSelection = async (type) => {
    setDepositType(type);
    if (type === 'external') {
      // Fetch wallet address for external deposit
      try {
        const res = await API.get('user/getWalletAddress');
        setDepositWalletAddress(res.data.data.address);
        setDepositProgress(1);
      } catch (error) {
        console.log('err: ', error);
        setNotification({
          message: 'Error al obtener la dirección de la billetera',
          type: 'error',
        });
      }
    } else if (type === 'pse') {
      // For PSE, move to amount input
      setDepositProgress(1);
    } else {
      // For internal transfer
      setDepositProgress(1);
    }
  };

  const handleDepositVaultSelection = (vaultId) => {
    setDepositSelectedVault(vaultId);
    setDepositProgress(2);
  };

  const handleDepositPSEPurchaseOrder = async () => {
    if (depositSubmitting) return;

    if (!depositCopValue || parseFloat(depositCopValue) <= 0) {
      setNotification({
        message: 'Por favor ingrese un monto válido',
        type: 'error',
      });
      return;
    }

    setDepositSubmitting(true);

    try {
      const res = await API.post('transaction/onRampByPayvalida', {
        amount: depositCopValue,
        exchangeRate: depositExchangeRate,
        assetAmount: depositAssetValue,
        assetId: depositAsset.assetId,
        displaySymbol: depositAsset.asset_id.displaySymbol,
        displayName: depositAsset.asset_id.displayName,
      });

      console.log('res: ', res);
      setDepositCheckoutLink(res.data.data.checkout);
      setDepositProgress(2);
      setDepositSubmitting(false);
    } catch (error) {
      console.log('err: ', error);
      setDepositSubmitting(false);
      const msg = error.response?.data?.message || error.message;
      setNotification({
        message: findMessage(msg),
        type: 'error',
      });
    }
  };

  const handleDepositPSECheckout = () => {
    const url = `https://${depositCheckoutLink}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setIsDepositModal(false);
    setDepositProgress(0);
  };

  const handleDepositSubmit = async () => {
    try {
      if (!depositAmount || parseFloat(depositAmount) <= 0) {
        setNotification({
          message: 'Por favor ingrese un monto válido',
          type: 'error',
        });
        return;
      }

      if (depositType === 'internal' && !depositSelectedVault) {
        setNotification({
          message: 'Por favor seleccione una bóveda de origen',
          type: 'error',
        });
        return;
      }

      const data = {
        assetId: depositAsset.assetId,
        amount: depositAmount,
        type: depositType,
      };

      if (depositType === 'internal') {
        data.fromVaultId = depositSelectedVault;
      } else {
        data.fromAddress = depositExternalAddress;
      }

      const res = await API.post('/assets/deposit', data);

      if (res.data.success == 'success') {
        setNotification({
          message: 'Depósito procesado exitosamente',
          type: 'success',
        });
        setIsDepositModal(false);
        getVautByUserId();
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      setNotification({
        message: findMessage(msg),
        type: 'error',
      });
    }
  };

  const onCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(depositAsset.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy!', err);
    }
  };

  const calculateFee = () => {
    if (!withdrawAmount || isNaN(withdrawAmount)) return '0.00';
    return ((parseFloat(withdrawAmount) * import.meta.env.VITE_DEVELOPER_FEE) / 100).toFixed(6);
  };

  const calculateTotal = () => {
    if (!withdrawAmount || isNaN(withdrawAmount)) return '0.00';
    const amount = parseFloat(withdrawAmount);
    const fee = (amount * import.meta.env.VITE_DEVELOPER_FEE) / 100;
    return (amount + fee).toFixed(6);
  };

  const getAssetDetails = (assetId) => {
    return assets.find((a) => a.legacyId === assetId) || {};
  };

  const filteredVaults = userVaults.filter(
    (v) =>
      v.name.toLowerCase().includes(searchVault.toLowerCase()) ||
      v.vault_id.toString().includes(searchVault)
  );

  const filteredDepositVaults = userVaults.filter(
    (v) =>
      v.name.toLowerCase().includes(depositSearchVault.toLowerCase()) ||
      v.vault_id.toString().includes(depositSearchVault)
  );

  const filteredAssets = assets.filter(
    (a) =>
      a.displayName?.toLowerCase().includes(searchAsset.toLowerCase()) ||
      a.displaySymbol?.toLowerCase().includes(searchAsset.toLowerCase()) ||
      a.legacyId?.toLowerCase().includes(searchAsset.toLowerCase())
  );

  const getExchangeRate = async () => {
    try {
      const res = await API.get('transaction/get_exchange_rate');
      if (res.data.success == true) {
        setDepositExchangeRate(res.data.rate);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Socket.IO setup - Listen for notifications
  useEffect(() => {
    // Initialize Socket.IO connection
    socketRef.current = io(import.meta.env.VITE_SERVER_URL, {
      transports: ['websocket', 'polling'],
      timeout: 10000,
      auth: {
        token: token,
        userId: snap.user.id,
      },
    });

    const socket = socketRef.current;

    // Connection event handlers
    socket.on('connect', () => {
      console.log('Assets component: Connected to socket server');
    });

    socket.on('disconnect', () => {
      console.log('Assets component: Disconnected from socket server');
    });

    socket.on('connect_error', (error) => {
      console.error('Assets component: Connection error:', error);
    });

    // Listen for notifications and refresh vaults
    socket.on('notification', (notification) => {
      console.log('Assets component: Notification received:', notification);
      // Call getUserVaults when notification is received

      getUserVaults();
      getVautByUserId();

      setNotification({
        message: notification.description,
        type: 'success',
      });
    });

    // Cleanup on component unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.off('connect');
        socketRef.current.off('disconnect');
        socketRef.current.off('connect_error');
        socketRef.current.off('notification');
        socketRef.current.disconnect();
      }
    };
  }, [snap.user.id, token]);

  // Fetch exchange rate on component mount
  useEffect(() => {
    getExchangeRate();
    getVautByUserId();
    getAllAssets();
    getUserVaults();
  }, []);

  // Calculate deposit asset value when COP value or exchange rate changes
  useEffect(() => {
    if (depositAsset && depositCopValue && depositExchangeRate) {
      const developerFee = parseFloat(import.meta.env.VITE_DEVELOPER_FEE || 0);
      // Convert COP to USD
      const usdValue = (depositCopValue / depositExchangeRate) * (1 - developerFee / 100.0);
      // Convert USD to asset amount
      const assetDetails = getAssetDetails(depositAsset.assetId);
      // Assuming USDC/USDT = 1:1 with USD, adjust if asset has different price
      const assetAmount = usdValue / assetUsdPrice;
      setDepositAssetValue(assetAmount.toFixed(6));
    } else {
      setDepositAssetValue('');
    }
  }, [depositCopValue, depositExchangeRate, depositAsset, assetUsdPrice]);

  return (
    <>
      <div className="relative px-10 py-8 space-y-8">
        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}

        <div className="sm:flex sm:border-b-1 sm:border-b-[#E9EAEB] items-center justify-between">
          <div className="font-[poppins] py-4 pr-2 space-y-1">
            <p className="text-xl font-semibold text-[#181D27]">Cadena de bloques</p>
            <p className="text-sm font-normal text-[#535862]">
              Consulta tu saldo y gestiona tus activos.
            </p>
          </div>
          <span className="space-x-3 flex">
            <IconButton
              disabled={false}
              bgColor="bg-[#092F7B]"
              icon={PlusIcon}
              text="Agregar activo"
              textColor="text-[#FDFDFD]"
              onClick={() => setIsAddModal(true)}
            />
          </span>
        </div>

        <div className="p-6 space-y-2 bg-white rounded-[20px] border-1 border-[#E6EBF7] shadow-xs">
          <div className="p-4 sm:p-6 rounded-[20px] bg-[#F9FAFB] border-1 border-[#D5D7DA]">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {/* Balance Section */}
              <div className="flex items-center space-x-2">
                <div className="w-[46px] h-[46px] flex items-center justify-center flex-shrink-0">
                  <img src={LogoIcon} alt="logo" className="w-[38px] h-[35px]" />
                </div>
                <div className="space-y-1 font-[poppins]">
                  <p className="text-sm text-[#181D27] font-medium leading-[22px] tracking-[0%]">
                    Saldo acumulado
                  </p>
                  <p className="text-lg text-[#0A0D12] font-semibold leading-[20px] flex items-center space-x-1">
                    <span>${totalBalance.toFixed(3) || '0.00'}</span>
                    <span className="text-xs text-[#535862] font-medium leading-[14px]">
                      {vault.currency || 'USD'}
                    </span>
                  </p>
                </div>
              </div>

              {/* Internal Address Section */}
              {vault.internal_address && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
                  <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-[#E1E1E1] flex-1 sm:flex-initial">
                    <span className="text-xs text-[#535862] font-medium whitespace-nowrap">
                      Dirección interna:
                    </span>
                    <span className="text-xs text-[#252B37] font-mono truncate max-w-[150px] sm:max-w-[200px]">
                      {vault.internal_address}
                    </span>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(vault.internal_address);
                        setNotification({
                          message: 'Dirección copiada al portapapeles',
                          type: 'success',
                        });
                      } catch (err) {
                        console.error('Failed to copy!', err);
                        setNotification({
                          message: 'Error al copiar la dirección',
                          type: 'error',
                        });
                      }
                    }}
                    className="px-3 py-2 text-xs font-medium text-[#092F7B] bg-white border border-[#092F7B] rounded-lg hover:bg-[#E6EBF7] transition-colors whitespace-nowrap"
                  >
                    Copiar
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2">
            <div className="bg-white rounded-[20px] px-4 py-6 space-y-8">
              <div>
                {!vault.assets?.length && (
                  <div className="py-6 space-y-2 bg-[#FDFDFD] border-1 border-[#F5F5F5] rounded-[10px]">
                    <div className="flex items-center justify-center">
                      <div className="w-9 h-9 bg-[#E6EBF7] rounded-full flex items-center justify-center">
                        <img src={LogoIcon} className="w-5 h-5" />
                      </div>
                    </div>
                    <p className="text-center text-sm font-medium text-[#535862]">
                      Todavía no tienes ningún activo registrado
                    </p>
                    <p className="text-center text-sm font-normal text-[#717680]">
                      Aquí verás todos tus activos una vez que agregues algunos.
                    </p>
                  </div>
                )}

                {vault.assets?.length > 0 && (
                  <div className="relative overflow-x-auto border-1 border-[#E1E1E1] sm:rounded-lg">
                    <table className="w-full font-[poppins] text-sm text-left rtl:text-right text-[#535862]">
                      <thead className="border-b-1 border-b-[#E1E1E1] bg-[#F9FAFB]">
                        <tr>
                          {Header.map((name, idx) => (
                            <th key={idx} scope="col" className="px-5 py-4.5">
                              <div className="flex items-center space-x-3">
                                <p className="font-medium text-[#535862]">{name}</p>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {vault.assets
                          .filter((a) => a.asset_id && a.assetId && a.asset_id.assetClass !== 'NATIVE')
                          .map((item, idx) => {
                            const assetDetails = getAssetDetails(item.assetId);
                            return (
                              <tr
                                key={idx}
                                className="border-b-1 border-b-[#E1E1E1] hover:bg-[#F9FAFB]"
                              >
                                <td className="p-5 min-w-30">
                                  <div className="flex items-center space-x-3">
                                    <div className="relative w-10 h-10 p-0.5">
                                      {/* Main Asset Image */}
                                      <div className="w-full h-full rounded-full bg-[#E6EBF7] flex items-center justify-center overflow-hidden">
                                        {assetDetails.metadata?.media?.[0] ? (
                                          <img
                                            src={assetDetails.metadata.media[0].url}
                                            alt={assetDetails.displayName}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                              e.target.onerror = null;
                                              e.target.src = LogoIcon;
                                            }}
                                          />
                                        ) : (
                                          <img src={LogoIcon} alt="asset" className="w-6 h-6" />
                                        )}
                                      </div>

                                      {/* Network Badge Overlay */}
                                      <div className="absolute bottom-0 right-0 w-4 h-4 rounded-full bg-white border border-[#E6EBF7] flex items-center justify-center overflow-hidden">
                                        <img
                                          src={GettingNativeAsset(assetDetails.legacyId).icon}
                                          alt={assetDetails.displayName}
                                          className="w-3 h-3 object-contain"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      </div>
                                    </div>

                                    <div>
                                      <p className="text-sm text-[#252B37] leading-[20px] font-medium">
                                        {assetDetails.displayName ||
                                          assetDetails.onchain?.name ||
                                          'Unknown Asset'}
                                      </p>
                                      <p className="text-xs text-[#535862]">
                                        {assetDetails.displaySymbol ||
                                          assetDetails.onchain?.symbol ||
                                          ''}
                                      </p>
                                    </div>
                                  </div>
                                </td>
                                <td className="p-5">
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <p className="px-1.5 sm:px-2 py-0.5 bg-[#D1FAE5] text-[#065F46] rounded text-[10px] sm:text-xs font-mono font-medium">
                                      {item.asset_id.displaySymbol}
                                    </p>
                                    <span className="text-[#6B7280]">-</span>
                                    <p className="px-1.5 sm:px-2 py-0.5 bg-[#DBEAFE] text-[#1E40AF] rounded text-[10px] sm:text-xs font-mono font-medium">
                                      {GettingNativeAsset(item.assetId).assetName || 'N/A'}
                                    </p>
                                  </div>
                                </td>
                                <td className="p-5">
                                  <p className="text-sm text-[#252B37] leading-[20px] font-semibold">
                                    {item.balance?.toFixed(6) || '0.000000'}
                                  </p>
                                  <p className="text-xs text-[#535862]">
                                    {assetDetails.displaySymbol || ''}
                                  </p>
                                </td>
                                <td className="p-5">
                                  <div className="flex items-center space-x-3">
                                    <button
                                      onClick={() => handleWithdraw(item)}
                                      className="group relative p-2 rounded-lg bg-[#FEF3F2] hover:bg-[#FEE4E2] transition-colors"
                                      title="Retirar"
                                    >
                                      Retirar
                                    </button>

                                    <button
                                      onClick={() => handleDeposit(item)}
                                      className="group relative p-2 rounded-lg bg-[#ECFDF3] hover:bg-[#D1FADF] transition-colors"
                                      title="Depositar"
                                    >
                                      Depositar
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {isAddModal && (
              <AddAssetModal
                data={assets}
                onSelectAsset={setSelectedAsset}
                onClose={() => setIsAddModal(false)}
                initialSelectedAsset={assets[0]}
                handleConfirmSelection={handleConfirmSelection}
                selectedAsset={selectedAsset}
                isSubmitting={isSubmitting}
              />
            )}

            {/* Withdraw Modal */}
            {isWithdrawModal && withdrawAsset && !showConfirmModal && !showPhoneVerifyModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-[20px] p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-[#181D27]">Retirar Activo</h2>
                    <button
                      onClick={() => setIsWithdrawModal(false)}
                      className="text-[#535862] hover:text-[#181D27] transition-colors"
                    >
                      <X size={24} />
                    </button>
                  </div>

                  {/* Asset Info */}
                  <div className="mb-6 p-4 bg-[#F9FAFB] rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-full bg-[#E6EBF7] flex items-center justify-center">
                        <img
                          src={
                            getAssetDetails(withdrawAsset.assetId)?.metadata?.media?.[0]?.url ||
                            LogoIcon
                          }
                          alt="asset"
                          className="w-8 h-8"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = LogoIcon;
                          }}
                        />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#252B37]">
                          {getAssetDetails(withdrawAsset.assetId).displayName || 'Asset'}
                        </p>
                        <p className="text-xs text-[#535862]">
                          Balance disponible: {withdrawAsset.balance?.toFixed(6) || '0.000000'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="flex space-x-2 mb-6 border-b-1 border-[#E1E1E1]">
                    <button
                      onClick={() => setWithdrawTab('internal')}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${
                        withdrawTab === 'internal'
                          ? 'text-[#092F7B] border-b-2 border-[#092F7B]'
                          : 'text-[#535862] hover:text-[#092F7B]'
                      }`}
                    >
                      Transferencia interna
                    </button>
                    <button
                      onClick={() => setWithdrawTab('external')}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${
                        withdrawTab === 'external'
                          ? 'text-[#092F7B] border-b-2 border-[#092F7B]'
                          : 'text-[#535862] hover:text-[#092F7B]'
                      }`}
                    >
                      {`Externo(${GettingNativeAsset(withdrawAsset.assetId).assetName})`}
                    </button>
                  </div>

                  {/* Internal Tab */}
                  {withdrawTab === 'internal' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[#252B37] mb-2">
                          Activo seleccionado
                        </label>
                        <div className="p-3 border-1 border-[#E1E1E1] rounded-lg bg-[#F9FAFB]">
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 rounded-full bg-[#E6EBF7] flex items-center justify-center">
                              <img
                                src={
                                  getAssetDetails(withdrawAsset.assetId)?.metadata?.media?.[0]
                                    ?.url || LogoIcon
                                }
                                alt="asset"
                                className="w-5 h-5"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = LogoIcon;
                                }}
                              />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-[#252B37]">
                                {getAssetDetails(withdrawAsset.assetId).displayName || 'Asset'}
                              </p>
                              <p className="text-xs text-[#535862]">
                                {GettingNativeAsset(withdrawAsset.assetId).assetName}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#252B37] mb-2">
                          Dirección de bóveda de destino
                        </label>
                        <input
                          type="text"
                          value={internalVaultAddress}
                          onChange={(e) => setInternalVaultAddress(e.target.value)}
                          placeholder="Ingrese el dirección de la bóveda..."
                          className="w-full px-4 py-2 border-1 border-[#E1E1E1] rounded-lg focus:outline-none focus:border-[#092F7B] font-mono text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#252B37] mb-2">
                          Monto
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={withdrawAmount}
                            onChange={(e) => setWithdrawAmount(e.target.value)}
                            placeholder="0.00"
                            step="0.000001"
                            min="0"
                            max={withdrawAsset.balance}
                            className="w-full px-4 py-2 border-1 border-[#E1E1E1] rounded-lg focus:outline-none focus:border-[#092F7B]"
                          />
                          <span className="absolute right-8 top-1/2 transform -translate-y-1/2 text-sm text-[#535862]">
                            {getAssetDetails(withdrawAsset.assetId).displaySymbol || ''}
                          </span>
                        </div>
                      </div>

                      <div className="p-4 bg-[#F9FAFB] rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-[#535862]">{`Comisión (${
                            withdrawTab === 'internal' ? 0.0 : 0.5
                          }%)`}</span>
                          <span className="text-sm font-medium text-[#252B37]">
                            {withdrawTab === 'internal' ? 0.0 : calculateFee()}{' '}
                            {getAssetDetails(withdrawAsset.assetId).displaySymbol || ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* External Tab */}
                  {withdrawTab === 'external' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[#252B37] mb-2">
                          Activo seleccionado
                        </label>
                        <div className="p-3 border-1 border-[#E1E1E1] rounded-lg bg-[#F9FAFB]">
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 rounded-full bg-[#E6EBF7] flex items-center justify-center">
                              <img
                                src={
                                  getAssetDetails(withdrawAsset.assetId)?.metadata?.media?.[0]
                                    ?.url || LogoIcon
                                }
                                alt="asset"
                                className="w-5 h-5"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = LogoIcon;
                                }}
                              />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-[#252B37]">
                                {getAssetDetails(withdrawAsset.assetId).displayName || 'Asset'}
                              </p>
                              <p className="text-xs text-[#535862]">
                                {GettingNativeAsset(withdrawAsset.assetId).assetName}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#252B37] mb-2">
                          Dirección externa
                        </label>
                        <input
                          type="text"
                          value={externalAddress}
                          onChange={(e) => setExternalAddress(e.target.value)}
                          placeholder={
                            GettingNativeAsset(withdrawAsset.assetId).assetName === 'Solana'
                              ? 'Ej: dirección en Solana'
                              : 'Ej: dirección en Polygon (0x...)'
                          }
                          className="w-full px-4 py-2 border-1 border-[#E1E1E1] rounded-lg focus:outline-none focus:border-[#092F7B] font-mono text-sm"
                        />
                        <p className="mt-2 text-xs text-[#535862]">
                          La dirección externa debe ser una dirección en{' '}
                          {GettingNativeAsset(withdrawAsset.assetId).assetName}.
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#252B37] mb-2">
                          Monto
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={withdrawAmount}
                            onChange={(e) => setWithdrawAmount(e.target.value)}
                            placeholder="0.00"
                            step="0.000001"
                            min="0"
                            max={withdrawAsset.balance}
                            className="w-full px-4 py-2 border-1 border-[#E1E1E1] rounded-lg focus:outline-none focus:border-[#092F7B]"
                          />
                          <span className="absolute right-8 top-1/2 transform -translate-y-1/2 text-sm text-[#535862]">
                            {getAssetDetails(withdrawAsset.assetId).displaySymbol || ''}
                          </span>
                        </div>
                      </div>

                      <div className="p-4 bg-[#F9FAFB] rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-[#535862]">{`Comisión (${
                            withdrawTab === 'internal' ? 0.0 : 0.5
                          }%)`}</span>
                          <span className="text-sm font-medium text-[#252B37]">
                            {withdrawTab === 'internal' ? 0.0 : calculateFee()}{' '}
                            {getAssetDetails(withdrawAsset.assetId).displaySymbol || ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-6">
                    <button
                      onClick={handleShowConfirmation}
                      className="w-full px-4 py-3 text-sm font-medium text-white bg-[#092F7B] rounded-lg hover:bg-[#0a3a94] transition-colors"
                    >
                      Confirmar retiro
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Confirmation Modal */}
            {showConfirmModal && withdrawAsset && (
              <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-3 sm:p-4">
                <div className="bg-white rounded-[16px] sm:rounded-[20px] p-4 sm:p-6 max-w-md w-full max-h-[95vh] overflow-y-auto">
                  <div className="flex flex-col items-center mb-4 sm:mb-6">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#FEF3F2] rounded-full flex items-center justify-center mb-3 sm:mb-4">
                      <ArrowUpFromLine size={24} className="text-[#D92D20] sm:w-8 sm:h-8" />
                    </div>
                    <h2 className="text-lg sm:text-xl font-semibold text-[#181D27] mb-1 sm:mb-2">
                      Confirmar Retiro
                    </h2>
                    <p className="text-xs sm:text-sm text-[#535862] text-center px-2">
                      Revisa los detalles de tu transacción antes de continuar
                    </p>
                  </div>

                  <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                    {/* Asset Info */}
                    <div className="p-3 sm:p-4 bg-[#F9FAFB] rounded-lg space-y-2 sm:space-y-3">
                      <div className="flex items-center space-x-2 sm:space-x-3 pb-2 sm:pb-3 border-b border-[#E1E1E1]">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#E6EBF7] flex items-center justify-center flex-shrink-0">
                          {getAssetImage(withdrawAsset) ? (
                            <img
                              src={
                                getAssetDetails(withdrawAsset.assetId)?.metadata?.media?.[0]?.url ||
                                LogoIcon
                              }
                              alt="asset"
                              className="w-5 h-5 sm:w-6 sm:h-6"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = LogoIcon;
                              }}
                            />
                          ) : (
                            <span className="text-xs font-semibold text-[#6B7280]">
                              {withdrawAsset?.displaySymbol?.substring(0, 2)}
                            </span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm font-medium text-[#252B37] truncate">
                            {getAssetDetails(withdrawAsset.assetId).displayName || 'Asset'}
                          </p>
                          <p className="text-[10px] sm:text-xs text-[#535862]">
                            {getAssetDetails(withdrawAsset.assetId).displaySymbol || ''}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-[10px] sm:text-xs text-[#535862] flex-shrink-0">
                            Tipo de transferencia
                          </span>
                          <span className="text-xs sm:text-sm font-medium text-[#252B37] text-right">
                            {withdrawTab === 'internal' ? 'Interna' : 'Externa (Fireblocks)'}
                          </span>
                        </div>

                        <div className="flex justify-between items-start gap-2">
                          <span className="text-[10px] sm:text-xs text-[#535862] flex-shrink-0">
                            Destino
                          </span>
                          <span className="text-xs sm:text-sm font-medium text-[#252B37] font-mono break-all text-right max-w-[65%] sm:max-w-[60%]">
                            {withdrawTab === 'internal'
                              ? `Bóveda: ${internalVaultAddress}`
                              : `${externalAddress.substring(0, 6)}...${externalAddress.substring(
                                  externalAddress.length - 4
                                )}`}
                          </span>
                        </div>

                        <div className="flex justify-between items-center pt-2 border-t border-[#E1E1E1]">
                          <span className="text-[10px] sm:text-xs text-[#535862]">Monto</span>
                          <span className="text-xs sm:text-sm font-semibold text-[#252B37]">
                            {withdrawAmount}{' '}
                            {getAssetDetails(withdrawAsset.assetId).displaySymbol || ''}
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-[10px] sm:text-xs text-[#535862]">
                            {`Comisión (${withdrawTab === 'internal' ? 0.0 : 0.5}%)`}
                          </span>
                          <span className="text-xs sm:text-sm font-medium text-[#252B37]">
                            {withdrawTab === 'internal' ? '0.00' : calculateFee()}{' '}
                            {getAssetDetails(withdrawAsset.assetId).displaySymbol || ''}
                          </span>
                        </div>

                        <div className="flex justify-between items-center pt-2 border-t border-[#E1E1E1]">
                          <span className="text-xs sm:text-sm font-semibold text-[#181D27]">
                            Total a deducir
                          </span>
                          <span className="text-sm sm:text-base font-bold text-[#D92D20]">
                            {withdrawTab === 'internal' ? '0.00' : calculateTotal()}{' '}
                            {getAssetDetails(withdrawAsset.assetId).displaySymbol || ''}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Warning */}
                    <div className="bg-[#FEF3F2] border border-[#FEE4E2] rounded-lg p-2.5 sm:p-3">
                      <p className="text-[10px] sm:text-xs text-[#B42318] font-medium leading-relaxed">
                        ⚠️ Esta acción no se puede deshacer. Asegúrate de que todos los detalles
                        sean correctos antes de continuar.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <button
                      onClick={() => setShowConfirmModal(false)}
                      disabled={sendingCode}
                      className="w-full sm:flex-1 px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium text-[#535862] bg-white border border-[#E1E1E1] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={onPhoneVerify}
                      disabled={sendingCode}
                      className="w-full sm:flex-1 px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium text-white bg-[#D92D20] rounded-lg hover:bg-[#B42318] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      {sendingCode ? (
                        <>
                          <svg
                            className="animate-spin h-3.5 w-3.5 sm:h-4 sm:w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span>Enviando...</span>
                        </>
                      ) : (
                        <span>Verificar y Enviar</span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Phone Verification Modal */}
            {showPhoneVerifyModal && (
              <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4">
                <div className="bg-white rounded-[20px] p-6 max-w-md w-full">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-[#181D27]">
                      Verificación de teléfono
                    </h2>
                    <button
                      onClick={() => {
                        setShowPhoneVerifyModal(false);
                        setShowConfirmModal(true);
                      }}
                      className="text-[#535862] hover:text-[#181D27] transition-colors"
                    >
                      <X size={24} />
                    </button>
                  </div>

                  <div className="mb-6">
                    <p className="text-sm text-[#535862] text-center mb-2">
                      Ingresa el código de 6 dígitos enviado a
                    </p>
                    <p className="text-sm font-medium text-[#181D27] text-center">
                      {snap.user.phonenumber?.nationality?.area_code}{' '}
                      {snap.user.phonenumber?.number}
                    </p>
                  </div>

                  <div className="flex justify-center space-x-2 mb-6">
                    {verifyCode.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (verifyInputRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleVerifyCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleVerifyCodeKeyDown(index, e)}
                        className={`w-12 h-12 text-center text-lg font-semibold border-2 rounded-lg focus:outline-none transition-colors ${
                          verifyError
                            ? 'border-[#D92D20] bg-[#FEF3F2]'
                            : 'border-[#E1E1E1] focus:border-[#092F7B]'
                        }`}
                      />
                    ))}
                  </div>

                  {verifyError && (
                    <div className="mb-4 p-3 bg-[#FEF3F2] border border-[#FEE4E2] rounded-lg">
                      <p className="text-xs text-[#B42318] text-center font-medium">
                        Código incorrecto. Por favor intenta de nuevo.
                      </p>
                    </div>
                  )}

                  <button
                    onClick={onConfirmPhoneVerify}
                    disabled={verifyingCode || verifyCode.join('').length !== 6}
                    className="w-full px-4 py-3 text-sm font-medium text-white bg-[#092F7B] rounded-lg hover:bg-[#0a3a94] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {verifyingCode ? (
                      <>
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span>Verificando...</span>
                      </>
                    ) : (
                      <span>Verificar código</span>
                    )}
                  </button>

                  <button
                    onClick={onPhoneVerify}
                    disabled={sendingCode}
                    className="w-full mt-3 px-4 py-2 text-sm font-medium text-[#092F7B] hover:text-[#0a3a94] transition-colors disabled:opacity-50"
                  >
                    {sendingCode ? 'Reenviando...' : 'Reenviar código'}
                  </button>
                </div>
              </div>
            )}

            {/* Deposit Modal */}
            {isDepositModal && depositAsset && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-[20px] p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
                  {/* Progress 0: Select deposit type */}
                  {depositProgress === 0 && (
                    <>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 space-y-2 mb-6">
                          <ComeBack text="Depositar" onClick={() => setIsDepositModal(false)} />
                          <p className="text-sm text-[#535862] font-normal leading-[20px]">
                            Selecciona el método de depósito
                          </p>
                        </div>

                        <button
                          onClick={() => setIsDepositModal(false)}
                          className="text-[#535862] hover:text-[#181D27] transition-colors"
                          aria-label="Close deposit modal"
                        >
                          <X size={24} />
                        </button>
                      </div>

                      {/* Asset Info */}
                      <div className="mb-6 p-4 bg-[#F9FAFB] rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 rounded-full bg-[#E6EBF7] flex items-center justify-center">
                            <img
                              src={
                                getAssetDetails(depositAsset.assetId)?.metadata?.media?.[0]?.url ||
                                LogoIcon
                              }
                              alt="asset"
                              className="w-8 h-8"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = LogoIcon;
                              }}
                            />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-[#252B37]">
                              {getAssetDetails(depositAsset.assetId).displayName || 'Asset'}
                            </p>
                            <p className="text-xs text-[#535862]">
                              Balance actual: {depositAsset.balance?.toFixed(6) || '0.000000'}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <button
                          onClick={() => handleDepositTypeSelection('pse')}
                          className="w-full p-4 bg-white border border-[#E1E1E1] rounded-lg hover:bg-[#F9FAFB] hover:border-[#092F7B] transition-colors text-left"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-[#E6EBF7] rounded-full flex items-center justify-center overflow-hidden">
                                <img
                                  src={PseIcon}
                                  className="w-full h-full object-cover"
                                  alt="PSE"
                                />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-[#252B37]">PSE</p>
                                <p className="text-xs text-[#535862]">(Pagos seguros en línea)</p>
                              </div>
                            </div>
                          </div>
                        </button>

                        <button
                          onClick={() => handleDepositTypeSelection('external')}
                          className="w-full p-4 bg-white border border-[#E1E1E1] rounded-lg hover:bg-[#F9FAFB] hover:border-[#092F7B] transition-colors text-left"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-[#092F7B] rounded-full flex items-center justify-center">
                                <BlockchainIcon />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-[#252B37]">
                                  Dirección de blockchain
                                </p>
                                <p className="text-xs text-[#535862]">
                                  Desde una billetera externa
                                </p>
                              </div>
                            </div>
                          </div>
                        </button>
                      </div>
                    </>
                  )}

                  {/* Progress 1: Internal - Select vault OR External - Show address OR PSE - Enter amount */}
                  {depositProgress === 1 && depositType === 'pse' && (
                    <>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 space-y-2 mb-6">
                          <ComeBack
                            text="Depositar a través de PSE"
                            onClick={() => setIsDepositModal(false)}
                          />
                          <p className="text-sm text-[#535862] font-normal leading-[20px]">
                            Ingresa el monto que deseas depositar
                          </p>
                        </div>

                        <button
                          onClick={() => setIsDepositModal(false)}
                          className="text-[#535862] hover:text-[#181D27] transition-colors"
                          aria-label="Close deposit modal"
                        >
                          <X size={24} />
                        </button>
                      </div>

                      <div className="space-y-4 mb-6">
                        <div>
                          <label className="block text-sm font-medium text-[#252B37] mb-2">
                            Envías
                          </label>
                          <div className="relative">
                            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
                              <img src={colombiaFlag} alt="Colombia" className="w-5 h-5" />
                              <span className="text-sm font-medium text-[#252B37]">COP</span>
                            </div>
                            <input
                              type="number"
                              value={depositCopValue}
                              onChange={(e) => setDepositCopValue(e.target.value)}
                              placeholder="0.00"
                              className="w-full pl-24 pr-4 py-3 border-1 border-[#E1E1E1] rounded-lg focus:outline-none focus:border-[#092F7B] text-right"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-[#252B37] mb-2">
                            Recibes
                          </label>
                          <div className="relative">
                            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
                              <div className="w-5 h-5 rounded-full bg-[#E6EBF7] flex items-center justify-center overflow-hidden">
                                <img
                                  src={
                                    getAssetDetails(depositAsset.assetId)?.metadata?.media?.[0]
                                      ?.url || LogoIcon
                                  }
                                  alt="asset"
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = LogoIcon;
                                  }}
                                />
                              </div>
                              <span className="text-sm font-medium text-[#252B37]">
                                {getAssetDetails(depositAsset.assetId).displaySymbol || 'Asset'}
                              </span>
                            </div>
                            <input
                              type="number"
                              value={depositAssetValue}
                              disabled
                              placeholder="0.000000"
                              className="w-full pl-32 pr-4 py-3 border-1 border-[#E1E1E1] rounded-lg bg-[#F9FAFB] text-right"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2 mb-6 text-xs text-[#414651] font-medium leading-[20px]">
                        <ExchangeRate
                          displaySymbol={depositAsset.asset_id.displaySymbol}
                          colRate={depositExchangeRate}
                          rate={depositExchangeRate}
                        />
                        <p className="text-xs font-medium text-center">
                          La cotización se actualizará en{' '}
                          <span className="text-[#07276B]">50 segundos</span>
                        </p>
                      </div>

                      <div className="flex justify-end">
                        <NormalButton
                          customClass="bg-[#092F7B] text-white"
                          text={depositSubmitting ? 'Procesando...' : 'Continuar'}
                          onClick={handleDepositPSEPurchaseOrder}
                          disabled={depositSubmitting}
                        />
                      </div>
                    </>
                  )}

                  {depositProgress === 1 && depositType === 'external' && (
                    <>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 space-y-2 mb-6">
                          <ComeBack
                            text="Depositar desde dirección externa"
                            onClick={() => setIsDepositModal(false)}
                          />
                          <p className="text-sm text-[#535862] font-normal leading-[20px]">
                            Transfiere desde tu billetera externa a esta dirección
                          </p>
                        </div>

                        <button
                          onClick={() => setIsDepositModal(false)}
                          className="text-[#535862] hover:text-[#181D27] transition-colors"
                          aria-label="Close deposit modal"
                        >
                          <X size={24} />
                        </button>
                      </div>

                      <div className="space-y-4">
                        <div className="text-center space-y-2">
                          <p className="text-sm text-[#061F5B] font-medium leading-[22px]">
                            Dirección de depósito
                          </p>
                          <div className="flex items-center justify-center">
                            <div className="p-4 border border-[#A4B9E5] rounded-[20px]">
                              <QRCodeCanvas value={depositAsset.address} size={200} level="H" />
                            </div>
                          </div>
                        </div>

                        <div className="bg-[#F5F8FF] rounded-[10px] p-4 space-y-2">
                          <p className="text-xs text-[#252B37] font-normal leading-[20px]">
                            Dirección de billetera
                          </p>

                          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <p className="flex-1 bg-white px-3 py-2 text-sm text-[#414651] font-mono border border-[#535862] rounded-[10px] break-all">
                              {depositAsset.address}
                            </p>

                            <button
                              onClick={onCopyToClipboard}
                              className="bg-[#FDFDFD] px-3 py-2 text-sm text-[#2A59C6] font-medium border border-[#2A59C6] rounded-[10px] hover:bg-[#E6EBF7] transition-colors whitespace-nowrap"
                            >
                              {copied ? 'Copiada' : 'Copiar'}
                            </button>
                          </div>
                        </div>

                        <div className="bg-[#FEF3F2] border border-[#FEE4E2] rounded-lg p-4">
                          <p className="text-xs text-[#B42318] font-medium">
                            ⚠️ Importante: Solo envía{' '}
                            {getAssetDetails(depositAsset.assetId).displayName} (
                            {getAssetDetails(depositAsset.assetId).displaySymbol}) a esta dirección.
                            Enviar otros activos puede resultar en pérdida permanente.
                          </p>
                        </div>

                        <div className="flex justify-end">
                          <NormalButton
                            customClass="bg-[#092F7B] text-white"
                            text="Volver a inicio"
                            onClick={() => setIsDepositModal(false)}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Progress 2: PSE Checkout */}
                  {depositProgress === 2 && depositType === 'pse' && (
                    <>
                      <div className="space-y-2 mb-6">
                        <ComeBack
                          text="Depositar a través de PSE"
                          onClick={() => setDepositProgress(1)}
                        />
                      </div>

                      <div>
                        <div className="py-6 space-y-2 text-center">
                          <div className="flex items-center justify-center">
                            <div className="relative w-20 h-20 bg-[#E6EBF7] rounded-full flex items-center justify-center">
                              <img src={Pse2xIcon} alt="pse 2x image" className="w-[73px] h-65px" />
                              <div className="absolute bottom-0 right-2 w-6.5 h-6.5 bg-white rounded-full">
                                <ArrowCircleUpRightIcon size="w-6.5 h-6.5" />
                              </div>
                            </div>
                          </div>
                          <div className="py-2 space-y-2">
                            <p className="text-sm text-[#252B37] font-medium leading-[22px]">
                              Estás siendo redireccionado a la página oficial de PSE para completar
                              el pago
                            </p>
                            <p className="text-sm text-[#717680] font-normal leading-[20px]">
                              Una vez finalizado el proceso, podrás ver tu depósito reflejado en tu
                              cuenta.
                            </p>
                          </div>
                        </div>

                        <div className="flex justify-end">
                          <NormalButton
                            customClass="bg-[#092F7B] text-white"
                            text="Continuar"
                            onClick={handleDepositPSECheckout}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
